/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { jsx } from '@compiled/react';
import { defineMessages, useIntl } from 'react-intl';

import { FabricChannel } from '@atlaskit/analytics-listeners';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { cssMap } from '@atlaskit/css';
import { EVENT_TYPE } from '@atlaskit/editor-common/analytics';
import { useSharedPluginStateWithSelector } from '@atlaskit/editor-common/hooks';
import type { ExtractInjectionAPI, PublicPluginAPI } from '@atlaskit/editor-common/types';
import WarningIcon from '@atlaskit/icon/core/status-warning';
import { Box, Inline, Pressable } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import type { MalleableUiPlugin } from '../../malleableUiPluginType';
import {
	parseMauiDiagramLink,
	WHITEBOARD_PATH_REGEX,
} from '../../pm-plugins/detect-maui-link';
import { MauiEmbedFinalizeStatus } from '../../pm-plugins/maui-diagram-paste-plugin';

const MAUI_WHITEBOARD_EXPERIENCE_MANIFEST_ID = 'maui-whiteboard';

const messages = defineMessages({
	bannerNeedsFinalizeMessage: {
		id: 'editor-plugin-malleable-ui.MauiNativeEmbedBanner.bannerNeedsFinalizeMessage',
		defaultMessage:
			'Could not add diagram. Move to this space to make it visible to others and avoid automated removal.',
		description:
			'Banner copy shown when a MAUI whiteboard is still parented in a Rovo system space and needs to be moved to the current space. Calls out two consequences: (1) other people cannot see it, (2) it will be automatically removed if left in the temporary space.',
	},
	bannerFinalizingMessage: {
		id: 'editor-plugin-malleable-ui.MauiNativeEmbedBanner.bannerFinalizingMessage',
		defaultMessage: 'Moving diagram to this space…',
		description: 'Banner copy shown while the MAUI diagram is being moved to the current page.',
	},
	bannerFailedMessage: {
		id: 'editor-plugin-malleable-ui.MauiNativeEmbedBanner.bannerFailedMessage',
		defaultMessage: "We couldn't move this diagram to this page. Please try again.",
		description: 'Banner copy shown when moving the MAUI whiteboard to the current space failed.',
	},
	moveToPageButton: {
		id: 'editor-plugin-malleable-ui.MauiNativeEmbedBanner.moveToPageButton',
		defaultMessage: 'Move to Space',
		description:
			'Primary button label that moves (re-parents) the MAUI diagram to the current space.',
	},
	movingButton: {
		id: 'editor-plugin-malleable-ui.MauiNativeEmbedBanner.movingButton',
		defaultMessage: 'Moving…',
		description: 'Primary button label while the MAUI diagram move is in flight.',
	},
	tryAgainButton: {
		id: 'editor-plugin-malleable-ui.MauiNativeEmbedBanner.tryAgainButton',
		defaultMessage: 'Try again',
		description: 'Primary button label shown after a failed MAUI diagram move attempt.',
	},
});

const styles = cssMap({
	banner: {
		position: 'absolute',
		top: token('space.0'),
		left: token('space.0'),
		right: token('space.0'),
		// `card` z-index keeps this above embed chrome but below modals/flags.
		zIndex: 100,
		paddingBlock: token('space.050'),
		paddingInline: token('space.100'),
		backgroundColor: token('color.background.warning'),
		color: token('color.text.warning'),
		borderBottom: `${token('border.width')} solid ${token('color.border.warning')}`,
	},
	bannerContent: {
		width: '100%',
	},
	bannerMessage: {
		font: token('font.body.small'),
	},
	actionLink: {
		paddingTop: token('space.0'),
	paddingRight: token('space.0'),
	paddingBottom: token('space.0'),
	paddingLeft: token('space.0'),
		backgroundColor: token('color.background.neutral.subtle'),
		color: token('color.link'),
		font: token('font.body.small'),
		fontWeight: token('font.weight.regular'),
		textDecoration: 'none',
		'&:hover': {
			textDecoration: 'underline',
		},
		'&:active': {
			color: token('color.link.pressed'),
		},
		'&:disabled': {
			color: token('color.text.disabled'),
			textDecoration: 'none',
			cursor: 'progress',
		},
	},
});

type NativeEmbedOverlayRenderProps = {
	experienceId?: string;
	localId?: string;
	resolvedMeta?: unknown;
	url?: string;
};

type Props = NativeEmbedOverlayRenderProps & {
	api?: ExtractInjectionAPI<MalleableUiPlugin>;
};

const parseWhiteboardContentId = (url: string | undefined): string | undefined => {
	if (!url) {
		return undefined;
	}
	return WHITEBOARD_PATH_REGEX.exec(url)?.[2];
};

const shouldShowBanner = (status: MauiEmbedFinalizeStatus) =>
	status === MauiEmbedFinalizeStatus.NEEDS_FINALIZE ||
	status === MauiEmbedFinalizeStatus.FINALIZING ||
	status === MauiEmbedFinalizeStatus.FAILED;

const TEST_ID = 'native-embed-extension--rovo-system-space-banner';

export const MauiNativeEmbedBanner = ({
	api,
	experienceId,
	url,
}: Props): React.JSX.Element | null => {
	const intl = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const [isSubmitting, setIsSubmitting] = useState(false);
	const probedKeyRef = useRef<string | null>(null);
	// Tracks whether the banner is still mounted so we don't call
	// `setIsSubmitting(false)` after the component has been unmounted —
	// which happens when the in-flight finalize causes status to reach
	// FINALIZED and `shouldShowBanner` returns false.
	const mountedRef = useRef(true);
	useEffect(() => {
		mountedRef.current = true;
		return () => {
			mountedRef.current = false;
		};
	}, []);

	const isMauiWhiteboard = experienceId === MAUI_WHITEBOARD_EXPERIENCE_MANIFEST_ID;
	const contentId = useMemo(
		() => (isMauiWhiteboard ? parseWhiteboardContentId(url) : undefined),
		[isMauiWhiteboard, url],
	);
	// If the embed URL carries an `_rc` conversation-context payload (e.g.
	// the original Rovo Chat link), forward it so `finalizeAfterMove` can
	// take the convo-ai path. When absent (e.g. older links, or links
	// already moved/cleaned), the consumer falls back to the monolith
	// finalize endpoint.
	const conversationContext = useMemo(
		() => (isMauiWhiteboard && url ? parseMauiDiagramLink(url)?.conversationContext : undefined),
		[isMauiWhiteboard, url],
	);
	const status = useSharedPluginStateWithSelector(api, ['malleableUi'], (states) => {
		const entry = contentId
			? states.malleableUiState?.diagramPaste?.embedFinalizeStates?.[contentId]
			: undefined;
		return (entry?.status as MauiEmbedFinalizeStatus | undefined) ?? MauiEmbedFinalizeStatus.UNKNOWN;
	});
	const finalizeAction = api?.malleableUi?.actions?.finalizeMauiEmbedFromIndicator;

	// Probe-only auto-check: drives status from UNKNOWN → CHECKING →
	// NEEDS_FINALIZE | NOT_NEEDED. Does NOT call `finalizeAfterMove` — the
	// banner's primary button does that explicitly with `mode: 'finalize'`.
	useEffect(() => {
		if (!isMauiWhiteboard || !contentId || !url || !finalizeAction) {
			return;
		}
		const key = `${contentId}::${url}`;
		if (probedKeyRef.current === key) {
			return;
		}
		if (status !== MauiEmbedFinalizeStatus.UNKNOWN) {
			probedKeyRef.current = key;
			return;
		}
		probedKeyRef.current = key;
		createAnalyticsEvent?.({
			action: 'probed',
			actionSubject: 'mauiDiagram',
			actionSubjectId: 'embedFinalize',
			eventType: EVENT_TYPE.OPERATIONAL,
			attributes: { contentId },
		})?.fire(FabricChannel.editor);
		void finalizeAction({
			contentId,
			originalUrl: url,
			conversationContext,
			mode: 'probe',
		});
	}, [
		contentId,
		conversationContext,
		createAnalyticsEvent,
		finalizeAction,
		isMauiWhiteboard,
		status,
		url,
	]);

	const onMoveToPage = useCallback(async () => {
		if (!finalizeAction || !contentId || !url || isSubmitting) {
			return;
		}
		setIsSubmitting(true);
		try {
			await finalizeAction({
				contentId,
				originalUrl: url,
				conversationContext,
				mode: 'finalize',
			});
		} finally {
			if (mountedRef.current) {
				setIsSubmitting(false);
			}
		}
	}, [contentId, conversationContext, finalizeAction, isSubmitting, url]);

	if (!isMauiWhiteboard || !shouldShowBanner(status)) {
		return null;
	}

	const inFlight = isSubmitting || status === MauiEmbedFinalizeStatus.FINALIZING;
	const isFailed = status === MauiEmbedFinalizeStatus.FAILED;

	const messageId = inFlight
		? messages.bannerFinalizingMessage
		: isFailed
			? messages.bannerFailedMessage
			: messages.bannerNeedsFinalizeMessage;

	const buttonLabelId = inFlight
		? messages.movingButton
		: isFailed
			? messages.tryAgainButton
			: messages.moveToPageButton;

	return (
		<Box xcss={styles.banner} testId={`${TEST_ID}--wrapper`} role="status" aria-live="polite">
			<Inline xcss={styles.bannerContent} space="space.075" alignBlock="center">
				<WarningIcon
					label=""
					color="currentColor"
					spacing="spacious"
					testId={`${TEST_ID}--icon`}
				/>
				<Inline space="space.075" alignBlock="center" xcss={styles.bannerMessage}>
					<Box testId={`${TEST_ID}--message`}>{intl.formatMessage(messageId)}</Box>
					<Pressable
						xcss={styles.actionLink}
						isDisabled={inFlight}
						onClick={onMoveToPage}
						testId={`${TEST_ID}--action-button`}
					>
						{intl.formatMessage(buttonLabelId)}
					</Pressable>
				</Inline>
			</Inline>
		</Box>
	);
};

type MauiNativeEmbedBannerEditorAPI = PublicPluginAPI<[MalleableUiPlugin]>;

export const createMauiNativeEmbedBannerRenderer = (
	api?: MauiNativeEmbedBannerEditorAPI,
): ((props: NativeEmbedOverlayRenderProps) => React.JSX.Element) =>
	(props: NativeEmbedOverlayRenderProps) => (
		<MauiNativeEmbedBanner
			{...props}
			api={api as ExtractInjectionAPI<MalleableUiPlugin> | undefined}
		/>
	);
